import React, { useState, useContext, useEffect } from "react";
import {
  message,
  Card,
  Typography,
  Col,
  Button,
  Alert,
  Spin,
} from "antd";
import GenericForm from "../../components/GenericForm";
import { AppContext } from "../../context/AppContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { BorrowerService } from "../../utils/apiService";
import {
  ArrowLeftOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

const EditLoan = () => {
  const { company } = useContext(AppContext);
  const token = localStorage.getItem("token");
  const location = useLocation();
  const navigate = useNavigate();
  const { loanId } = useParams();
  
  const [loanTypes, setLoanTypes] = useState([]);
  const [borrowers, setBorrowers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [loanData, setLoanData] = useState(null);
  
  const { Title, Text } = Typography;
  
  // Get data from navigation state
  const isResubmission = location.state?.isResubmission || false;
  const rejectionReason = location.state?.rejectionReason || "";

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch loan data
        const loanResponse = await axios.get(
          `https://zedlenders.pickmesms.com/items/loans/${loanId}?fields=*.*`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setLoanData(loanResponse.data.data);

        // Fetch other required data
        await Promise.all([
          fetchBorrowers(),
          fetchLoanTypes(),
          fetchBranches(),
        ]);
      } catch (error) {
        console.error("Error fetching data:", error);
        message.error("Failed to load loan data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [loanId]);

  const fetchBorrowers = async () => {
    try {
      const data = await BorrowerService.fetchBorrowers(token, company.id);
      const people = data.map((item) => ({
        label: `${item.first_name} ${item.last_name} ~ Score: ${item.credit_score}`,
        value: item.id,
      }));
      setBorrowers(people);
    } catch (error) {
      console.error("Error fetching borrowers:", error);
    }
  };

  const fetchLoanTypes = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/loan_products?fields=*.*&filter[company]=${company.id}&filter[is_active][_eq]=true`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const types = response.data.data.map((item) => ({
        label: item.loan_name,
        value: item.id,
        ...item,
      }));
      setLoanTypes(types);
    } catch (error) {
      console.error("Error fetching loan types:", error);
    }
  };

  const fetchBranches = async () => {
    try {
      const response = await axios.get(
        `https://zedlenders.pickmesms.com/items/branches?filter[company]=${company.id}&filter[status]=published`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const branchOptions = response.data.data.map((branch) => ({
        label: branch.branch_name,
        value: branch.id,
      }));
      setBranches(branchOptions);
    } catch (error) {
      console.error("Error fetching branches:", error);
    }
  };

  const handleFinish = async (values) => {
    try {
      setSubmitting(true);
      
      // Update the loan with new values and change status back to pending approval
      await axios.patch(
        `https://zedlenders.pickmesms.com/items/loans/${loanId}`,
        {
          ...values,
          loan_status: 1, // Set back to "Pending Approval"
          resubmission_date: new Date().toISOString().split("T")[0],
          // Clear rejection fields
          rejection_reason: null,
          rejection_date: null,
          rejected_by: null,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      message.success("Loan updated and resubmitted for approval!");
      setTimeout(() => {
        navigate("/loans/1"); // Navigate to pending approval page
      }, 2000);
    } catch (error) {
      console.error("Error updating loan:", error);
      message.error("Failed to update loan");
    } finally {
      setSubmitting(false);
    }
  };

  const fields = [
    {
      label: "Borrower",
      name: "borrower",
      type: "select",
      placeholder: "Select Borrower",
      options: borrowers,
      rules: [{ required: true, message: "Please select borrower!" }],
      props: {
        showSearch: true,
        optionFilterProp: "children",
      },
    },
    {
      label: "Loan Type",
      name: "loan_product",
      type: "select",
      placeholder: "Select Loan Type",
      options: loanTypes,
      rules: [{ required: true, message: "Please select loan type!" }],
      props: {
        showSearch: true,
        optionFilterProp: "children",
      },
    },
    {
      label: "Branch",
      name: "branch",
      type: "select",
      placeholder: "Select Branch",
      options: branches,
      rules: [{ required: true, message: "Please select a branch!" }],
      props: {
        showSearch: true,
        optionFilterProp: "children",
      },
    },
    {
      label: "Amount (ZMW)",
      name: "amount",
      type: "number",
      placeholder: "Enter loan amount",
      rules: [{ required: true, message: "Please input loan amount!" }],
    },
  ];

  // Prepare initial values from loan data
  const getInitialValues = () => {
    if (!loanData) return {};
    return {
      borrower: loanData.borrower?.id || loanData.borrower,
      loan_product: loanData.loan_product?.id || loanData.loan_product,
      branch: loanData.branch?.id || loanData.branch,
      amount: loanData.amount,
    };
  };

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", marginTop: "20%" }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <Col xs={24} sm={16} md={16} lg={12} xl={12}>
      <Card>
        <div style={{ marginBottom: 16 }}>
          <Button
            type="link"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/loans/4")}
            style={{ marginBottom: 8 }}
          >
            Back to Rejected Loans
          </Button>
        </div>

        <Title level={3} style={{ textAlign: "center", marginBottom: 24 }}>
          {isResubmission ? "Edit & Resubmit Loan" : "Edit Loan"}
        </Title>

        {isResubmission && rejectionReason && (
          <Alert
            message="Loan Rejection Reason"
            description={rejectionReason}
            type="error"
            icon={<InfoCircleOutlined />}
            style={{ marginBottom: 24 }}
            showIcon
          />
        )}

        <GenericForm
          fields={fields}
          onFinish={handleFinish}
          layout="vertical"
          initialValues={getInitialValues()}
          submitButtonText={isResubmission ? "Resubmit for Approval" : "Update Loan"}
          submitButtonProps={{
            loading: submitting,
            disabled: submitting,
            style: { width: "100%" },
          }}
        />
      </Card>
    </Col>
  );
};

export default EditLoan;